import type {
  ArrowOpacityState,
  ArrowUI,
} from '@components/ArtBoard/Arrows/types'
import type { ID } from '@octopus-do/octopus-project-core'
import { selectBlockParent } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectTagVisibility } from '@store/reducers/tags/visibility'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { AppState } from '@/types'

export const useGetArrows = (): ArrowUI[] => {
  const selectedBlock = useSelector<AppState, string[] | false>(
    ({ canvas }) =>
      canvas.block.selectedToEditBlockId === null && canvas.block.ids
  )
  const selectedNodes = useSelector<AppState, string[]>(
    ({ canvas }) => canvas.node.selectedNodes
  )
  const selectedArrowId = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.arrow.selectedArrowId
  )

  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)
  const tabArrows = useSelector(
    migrationProjectSelector.getTabArrows(activeTab)
  )

  // Page-tag filter (OCT-5007): an arrow whose page is filtered off the
  // artboard has nothing to point at, so it goes with it. ArrowLine also
  // bails out on a missing DOM node, but only on mount — toggling the filter
  // afterwards would leave the arrow hanging.
  const { filterIsActive, visibleNodeIds } = useSelector(selectTagVisibility)
  const relations = useSelector(({ relations }: AppState) => relations)

  const nodes = useSelector(
    ({ manager }: AppState) => manager.project.nodes as { id: ID }[]
  )
  const nodeIds = useMemo(() => new Set(nodes.map((node) => node.id)), [nodes])

  // Начало стрелки — блок или сама страница (OCT-5140): у страницы искать
  // родителя не надо, иначе фильтр проверит видимость её предка.
  const getStartNodeId = (startId: string): string | undefined =>
    nodeIds.has(startId)
      ? startId
      : selectBlockParent(startId)({ relations })[0]

  const isOnArtboard = (relation: string[]): boolean => {
    if (!filterIsActive) {
      return true
    }

    const startNodeId = getStartNodeId(relation[0])

    return (
      visibleNodeIds.has(relation[1]) &&
      (!startNodeId || visibleNodeIds.has(startNodeId))
    )
  }

  const arrows = tabArrows.filter(
    (a) => a.visible && isOnArtboard(a.relation as unknown as string[])
  )

  // Выделенная страница подсвечивает свои стрелки так же, как блок — свои.
  // Блок в приоритете: выделяя его, пользователь мог оставить выделенной и
  // страницу, и подсветка обоих сразу читается как чужие связи.
  const selectedSources =
    Array.isArray(selectedBlock) && selectedBlock.length === 0
      ? selectedNodes
      : selectedBlock

  if (
    Array.isArray(selectedSources) &&
    (selectedSources.length > 0 || selectedArrowId)
  ) {
    const result = arrows.map((arrow) => {
      const opacityState: ArrowOpacityState =
        selectedSources.includes(arrow.relation[0]) ||
        selectedArrowId === arrow.id
          ? 'default'
          : 'dimmed'
      const isHighlighted = selectedSources.includes(arrow.relation[0])
      const isSelected = selectedArrowId === arrow.id

      return { ...arrow, opacityState, isHighlighted, isSelected }
    }) as ArrowUI[]

    if (result.every((arrow) => arrow.opacityState === 'dimmed')) {
      return result.map((arrow) => ({
        ...arrow,
        opacityState: 'default',
      }))
    }

    return result
  }

  return arrows.map((arrow) => ({
    ...arrow,
    opacityState: 'default',
    isSelected: false,
    isHighlighted: false,
  }))
}
